<template>
    <div>

        <!-- Filters -->
        <v-card outlined class="ai-history-card mb-4 pa-3">
            <v-row dense align="center">
                <template v-if="custom">
                    <v-col cols="6" sm="3" md="2">
                        <v-text-field :value="dateFrom" type="date" label="From" dense outlined hide-details @change="setDate('dateFrom', $event)" />
                    </v-col>
                    <v-col cols="6" sm="3" md="2">
                        <v-text-field :value="dateTo" type="date" label="To" dense outlined hide-details @change="setDate('dateTo', $event)" />
                    </v-col>
                </template>
                <v-col cols="12" sm="6" md="3">
                    <v-text-field
                        v-model="search"
                        label="Search client, connection or user"
                        outlined dense hide-details clearable
                        prepend-inner-icon="mdi-magnify"
                        @input="onSearch"
                    />
                </v-col>
                <v-col cols="12" sm="6" md="2">
                    <v-autocomplete
                        v-model="userId"
                        :items="userOptions"
                        item-text="label"
                        item-value="id"
                        label="User"
                        outlined dense hide-details clearable
                        prepend-inner-icon="mdi-account-outline"
                        @change="reload"
                    />
                </v-col>
                <v-col cols="12" sm="6" md="2">
                    <v-autocomplete
                        v-model="tool"
                        :items="toolOptions"
                        label="Tool used"
                        outlined dense hide-details clearable
                        prepend-inner-icon="mdi-wrench-outline"
                        @change="reload"
                    />
                </v-col>
                <v-col cols="6" sm="3" md="2">
                    <v-select v-model="client" :items="stats.client_options || []" label="Client" outlined dense hide-details clearable @change="reload" />
                </v-col>
                <v-col cols="6" sm="3" md="auto">
                    <v-checkbox v-model="changedOnly" label="Made changes" color="deep-purple" dense hide-details class="mt-0 pt-0" @change="reload" />
                </v-col>
                <v-col v-if="hasFilters" cols="auto">
                    <v-btn text small color="grey darken-1" @click="clearFilters"><v-icon small left>mdi-filter-off-outline</v-icon>Clear</v-btn>
                </v-col>
            </v-row>
        </v-card>

        <!-- Headline numbers -->
        <v-row dense class="mb-2">
            <v-col v-for="t in tiles" :key="t.label" cols="6" sm="4" md>
                <v-card outlined class="ai-history-card ai-history-tile">
                    <div class="ai-history-tile__label">
                        <v-icon small class="mr-1">{{ t.icon }}</v-icon>{{ t.label }}
                    </div>
                    <div class="ai-history-tile__value">
                        <v-progress-circular v-if="statsLoading && !statsLoaded" indeterminate size="18" width="2" color="grey" />
                        <template v-else>{{ t.value }}</template>
                    </div>
                    <div class="ai-history-tile__sub">{{ t.sub }}</div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Tool stats -->
        <v-row dense class="mb-2">
            <v-col v-for="chart in toolCharts" :key="chart.title" cols="12" md="6">
                <v-card outlined class="ai-history-card pa-4" style="height: 100%">
                    <div class="d-flex align-baseline mb-3">
                        <div class="subtitle-2 ai-history-ink">{{ chart.title }}</div>
                        <span class="caption grey--text ml-2">{{ chart.unit }}</span>
                    </div>
                    <div v-if="!chart.rows.length" class="caption grey--text py-6 text-center">No tools used in this period</div>
                    <div
                        v-for="row in chart.rows"
                        :key="row.tool"
                        class="ai-history-bar"
                        :class="{ 'ai-history-bar--active': tool === row.tool }"
                        :title="`${row.tool}: ${row.calls} call${row.calls === 1 ? '' : 's'} in ${row.sessions} session${row.sessions === 1 ? '' : 's'}, ${row.errors} error${row.errors === 1 ? '' : 's'}, ${seconds(row.avg_ms)} on average. Click to filter.`"
                        @click="toggleTool(row.tool)"
                    >
                        <div class="ai-history-bar__name">{{ row.tool }}</div>
                        <div class="ai-history-bar__track">
                            <div class="ai-history-bar__fill" :style="{ width: chart.width(row) }" />
                        </div>
                        <div class="ai-history-bar__value">{{ chart.value(row) }}</div>
                        <div class="ai-history-bar__sub">{{ chart.sub(row) }}</div>
                    </div>
                    <div v-if="chart.note" class="caption grey--text mt-3">{{ chart.note }}</div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Sessions -->
        <v-card outlined class="ai-history-card">
            <div class="caption grey--text px-4 pt-3">One row per connector session that called at least one tool. MCP clients run their own AI model, so there is no cost here; click a session to see each tool call.</div>
            <v-data-table
                :headers="headers"
                :items="sessions"
                :loading="sessionsLoading"
                :server-items-length="sessionsTotal"
                :options.sync="options"
                :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
                class="ai-history-table ai-history-table--rows"
                no-data-text="No MCP sessions match these filters"
                @update:options="loadSessions"
                @click:row="openSession"
            >
                <template v-slot:item.client_name="{ item }">
                    <div class="ai-history-convo">
                        <div class="ai-history-convo__title">{{ clientLabel(item) }}</div>
                        <div class="ai-history-convo__meta">
                            <span>{{ connectionLabel(item) }}</span>
                            <v-chip v-if="item.changed" x-small label color="green lighten-5" text-color="green darken-3" class="ml-1">
                                <v-icon x-small left>mdi-pencil-outline</v-icon>Made changes
                            </v-chip>
                            <v-chip v-if="item.errors" x-small label color="red lighten-5" text-color="red darken-3" class="ml-1">
                                <v-icon x-small left>mdi-alert-circle-outline</v-icon>{{ item.errors }} error{{ item.errors === 1 ? '' : 's' }}
                            </v-chip>
                        </div>
                    </div>
                </template>
                <template v-slot:item.user_name="{ item }">
                    <user-cell :name="item.user_name" :email="item.user_email" :none="!item.user_id" />
                    <div v-if="item.user_id" class="ai-history-chips mt-1">
                        <v-chip v-if="item.user_role" x-small label color="blue lighten-5" text-color="blue darken-3" title="User role">{{ item.user_role }}</v-chip>
                    </div>
                </template>
                <template v-slot:item.tools="{ item }">
                    <div class="ai-history-chips">
                        <v-chip
                            v-for="t in item.tools"
                            :key="t.name"
                            x-small
                            label
                            :color="tool === t.name ? 'deep-purple' : 'deep-purple lighten-5'"
                            :text-color="tool === t.name ? 'white' : 'deep-purple darken-2'"
                            :title="`${t.calls} call${t.calls === 1 ? '' : 's'}. Click to show only sessions that used it.`"
                            @click.stop="toggleTool(t.name)"
                        >
                            {{ t.name }}<span v-if="t.calls > 1" class="ml-1 ai-history-chip-count">×{{ t.calls }}</span>
                        </v-chip>
                    </div>
                </template>
                <template v-slot:item.duration="{ item }">{{ duration(item.started_at, item.last_activity_at) }}</template>
                <template v-slot:item.last_activity_at="{ item }">
                    <div class="ai-history-date">{{ formatDate(item.last_activity_at) }}</div>
                    <div class="caption grey--text">{{ formatTime(item.last_activity_at) }}</div>
                </template>
            </v-data-table>
        </v-card>

        <!-- One session -->
        <v-dialog v-model="dialog" max-width="920" scrollable>
            <v-card v-if="session" class="ai-history-dialog">
                <v-card-title class="d-block pb-2">
                    <div class="d-flex align-start">
                        <div style="min-width: 0">
                            <div class="ai-history-convo__title text-subtitle-1 font-weight-bold">{{ clientLabel(session) }}</div>
                            <div class="caption grey--text">
                                {{ session.user_name || 'No user (machine token)' }}<span v-if="session.user_email"> · {{ session.user_email }}</span>
                            </div>
                        </div>
                        <v-spacer />
                        <v-btn icon small @click="dialog = false"><v-icon small>mdi-close</v-icon></v-btn>
                    </div>
                    <div class="ai-history-facts mt-2">
                        <span><v-icon x-small>mdi-clock-outline</v-icon> {{ formatDate(session.started_at) }}, {{ formatTime(session.started_at) }} – {{ formatTime(session.last_activity_at) }}</span>
                        <span><v-icon x-small>mdi-wrench-outline</v-icon> {{ session.calls.length }} tool call{{ session.calls.length === 1 ? '' : 's' }}</span>
                        <span><v-icon x-small>mdi-connection</v-icon> {{ connectionLabel(session) }}</span>
                        <span v-if="session.protocol_version"><v-icon x-small>mdi-protocol</v-icon> MCP {{ session.protocol_version }}</span>
                        <span v-if="session.ip"><v-icon x-small>mdi-ip-network-outline</v-icon> {{ session.ip }}</span>
                    </div>
                </v-card-title>
                <v-divider />
                <v-card-text class="ai-history-transcript">
                    <div class="caption grey--text mb-3">What the client asked for. The results it got back are never stored.</div>
                    <div v-for="c in session.calls" :key="c.id" class="ai-history-msg ai-history-msg--assistant">
                        <div class="ai-history-msg__meta">
                            <v-icon x-small class="mr-1">mdi-wrench-outline</v-icon><strong>{{ c.tool }}</strong>
                            <span class="ml-1">{{ formatTime(c.created_at, true) }}</span>
                            <span v-if="c.changed" class="ai-history-tag ai-history-tag--changed">Made changes</span>
                            <span v-if="c.is_error" class="ai-history-tag ai-history-tag--error">Error</span>
                            <span class="ml-auto">
                                <template v-if="c.row_count !== null">{{ number(c.row_count) }} row{{ c.row_count === 1 ? '' : 's' }} · </template>{{ seconds(c.duration_ms) }}
                            </span>
                        </div>
                        <pre v-if="c.arguments" class="ai-history-args">{{ JSON.stringify(c.arguments, null, 2) }}</pre>
                        <div v-else class="caption grey--text">No arguments</div>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
// MCP connector sessions for the AI Assistant History page (admins only). Sessions come from
// /ai_usage/mcp_sessions, the headline numbers and tool charts from /ai_usage/mcp_stats (same
// filters), one session's tool calls from /ai_usage/mcp_sessions/{id}. The date range belongs to
// the parent page (.sync); the parent calls reload() for Refresh and preset changes.
import UserCell from './UserCell'
import userLocalTime from '@/utils/userLocalTime'

const TOP_TOOLS = 8

export default {
    mixins: [userLocalTime],
    name: 'McpSessionsPanel',
    components: { UserCell },
    props: {
        dateFrom: { type: String, default: '' },
        dateTo: { type: String, default: '' },
        custom: { type: Boolean, default: false },
    },
    data() {
        return {
            userId: null,
            tool: null,
            client: null,
            changedOnly: false,
            search: '',
            searchTimer: null,
            stats: { totals: {}, tools: [], tool_options: [], user_options: [], client_options: [] },
            statsLoading: false,
            statsLoaded: false,
            sessions: [],
            sessionsTotal: 0,
            sessionsLoading: false,
            options: { page: 1, itemsPerPage: 25 },
            session: null,
            dialog: false,
            headers: [
                { text: 'Client', value: 'client_name', sortable: false, width: '26%' },
                { text: 'User', value: 'user_name', sortable: false },
                { text: 'Tools used', value: 'tools', sortable: false, width: '32%' },
                { text: 'Tool calls', value: 'tool_calls', sortable: false, align: 'right' },
                { text: 'Duration', value: 'duration', sortable: false, align: 'right' },
                { text: 'Last activity', value: 'last_activity_at', sortable: false, align: 'right' },
            ],
        }
    },
    computed: {
        userOptions() {
            return (this.stats.user_options || []).map(u => ({ id: u.id, label: u.email ? `${u.name} (${u.email})` : u.name }))
        },
        toolOptions() {
            const options = this.stats.tool_options || []
            return this.tool && !options.includes(this.tool) ? [this.tool, ...options] : options
        },
        hasFilters() {
            return !!(this.userId || this.tool || this.client || this.changedOnly || this.search)
        },
        tiles() {
            const t = this.stats.totals || {}
            const sessions = t.sessions || 0
            const calls = t.tool_calls || 0
            return [
                { label: 'Sessions', icon: 'mdi-connection', value: this.number(sessions), sub: `${this.number(calls)} tool calls` },
                { label: 'People', icon: 'mdi-account-multiple-outline', value: this.number(t.users || 0), sub: sessions ? `${(sessions / Math.max(1, t.users)).toFixed(1)} sessions each` : '—' },
                { label: 'Tool calls', icon: 'mdi-wrench-outline', value: this.number(calls), sub: sessions ? `${(calls / sessions).toFixed(1)} per session` : '—' },
                { label: 'Made changes', icon: 'mdi-pencil-outline', value: this.number(t.changed || 0), sub: sessions ? `${Math.round(((t.changed || 0) / sessions) * 100)}% of sessions` : '—' },
                { label: 'Errors', icon: 'mdi-alert-circle-outline', value: this.number(t.errors || 0), sub: calls ? `${(((t.errors || 0) / calls) * 100).toFixed(1)}% of calls` : '—' },
            ]
        },
        toolCharts() {
            const tools = this.stats.tools || []
            const byCalls = tools.slice(0, TOP_TOOLS)
            const bySpeed = [...tools].sort((a, b) => b.avg_ms - a.avg_ms).slice(0, TOP_TOOLS)
            const maxCalls = Math.max(1, ...byCalls.map(r => r.calls))
            const maxMs = Math.max(1, ...bySpeed.map(r => r.avg_ms))
            return [
                {
                    title: 'Most used tools',
                    unit: 'calls',
                    rows: byCalls,
                    width: r => `${(r.calls / maxCalls) * 100}%`,
                    value: r => this.number(r.calls),
                    sub: r => `${r.sessions} sess.`,
                    note: tools.length > TOP_TOOLS ? `Top ${TOP_TOOLS} of ${tools.length} tools. Click a tool to filter the sessions.` : 'Click a tool to filter the sessions.',
                },
                {
                    title: 'Slowest tools',
                    unit: 'average run time',
                    rows: bySpeed,
                    width: r => `${(r.avg_ms / maxMs) * 100}%`,
                    value: r => this.seconds(r.avg_ms),
                    sub: r => (r.errors ? `${r.errors} err.` : ''),
                    note: 'Time GGB took to answer each call (the client\'s own thinking time is not included).',
                },
            ]
        },
    },
    mounted() {
        this.reload()
    },
    beforeDestroy() {
        clearTimeout(this.searchTimer)
    },
    methods: {
        setDate(key, val) {
            this.$emit(`update:${key}`, val)
            this.$nextTick(this.reload)
        },
        onSearch() {
            clearTimeout(this.searchTimer)
            this.searchTimer = setTimeout(this.reload, 400)
        },
        toggleTool(name) {
            this.tool = this.tool === name ? null : name
            this.reload()
        },
        clearFilters() {
            this.userId = null
            this.tool = null
            this.client = null
            this.changedOnly = false
            this.search = ''
            this.reload()
        },
        filterParams() {
            const params = { date_from: this.dateFrom, date_to: this.dateTo }
            if (this.userId) params.user_id = this.userId
            if (this.tool) params.tool = this.tool
            if (this.client) params.client = this.client
            if (this.changedOnly) params.changed = 1
            if (this.search) params.search = this.search
            return params
        },
        // Stats follow every filter; the table goes back to page 1 (a page change reloads it itself)
        reload() {
            if (!this.dateFrom || !this.dateTo) return
            this.loadStats()
            if (this.options.page !== 1) this.options = { ...this.options, page: 1 }
            else this.loadSessions()
        },
        async loadStats() {
            this.statsLoading = true
            this.emitLoading()
            const res = await this.$axios.get('api/ai_usage/mcp_stats', this.filterParams())
            this.statsLoading = false
            this.emitLoading()
            if (res?.data?.response) {
                this.stats = res.data
                this.statsLoaded = true
            } else {
                this.$toast.error(res?.data?.message || 'Could not load the MCP stats.')
            }
        },
        async loadSessions() {
            if (!this.dateFrom || !this.dateTo) return
            this.sessionsLoading = true
            this.emitLoading()
            const res = await this.$axios.get('api/ai_usage/mcp_sessions', { ...this.filterParams(), per_page: this.options.itemsPerPage, page: this.options.page })
            this.sessionsLoading = false
            this.emitLoading()
            if (res?.data?.response) {
                this.sessions = res.data.data
                this.sessionsTotal = res.data.total
            } else {
                this.$toast.error(res?.data?.message || 'Could not load the MCP sessions.')
            }
        },
        emitLoading() {
            this.$emit('loading', this.statsLoading || this.sessionsLoading)
        },
        async openSession(row) {
            const res = await this.$axios.get(`api/ai_usage/mcp_sessions/${row.id}`)
            if (res?.data?.response) {
                this.session = res.data.session
                this.dialog = true
            } else {
                this.$toast.error(res?.data?.message || 'Could not open the session.')
            }
        },
        // clientInfo from the client's initialize, else the OAuth app or token it connected with
        clientLabel(s) {
            if (s.client_name) return s.client_version ? `${s.client_name} ${s.client_version}` : s.client_name
            return s.oauth_app || s.token_name || 'Unknown client'
        },
        connectionLabel(s) {
            if (Number(s.token_id) > 0) return `Token: ${s.token_name || '#' + s.token_id}`
            return s.oauth_app ? `Sign-in: ${s.oauth_app}` : 'Sign-in'
        },
        duration(from, to) {
            const mins = from && to ? this.$date(to).diff(this.$date(from), 'minute') : 0
            if (mins < 1) return '< 1 min'
            return mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h ${mins % 60}m`
        },
        seconds(ms) {
            return `${(Number(ms || 0) / 1000).toFixed(1)}s`
        },
        number(val) {
            return Number(val || 0).toLocaleString()
        },
    },
}
</script>

<style scoped src="./ai-history.css"></style>
